import { useEffect, useState } from "react";
import { Building2, Plus, Copy, Check, Power, PlayCircle, Layers, Store, Users, FlaskConical, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useFeatures } from "@/context/FeatureContext";
import { TopNav } from "@/components/TopNav";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const Inp = (props) => (
  <input {...props} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-none focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-transparent" />
);
const Lbl = ({ children }) => (
  <span className="text-xs uppercase tracking-widest font-medium text-slate-500">{children}</span>
);

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 p-5">
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-widest font-medium text-slate-500">{label}</span>
      <Icon className="h-4 w-4 text-blue-700" />
    </div>
    <p className="text-3xl font-bold mt-2">{value}</p>
  </div>
);

export default function SuperAdminConsole() {
  const [tab, setTab] = useState("franchises");
  const [overview, setOverview] = useState(null);
  const [franchises, setFranchises] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", owner_email: "", owner_name: "" });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    api.get("/admin/overview").then(({ data }) => setOverview(data)).catch(() => {});
    api.get("/admin/franchises").then(({ data }) => setFranchises(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      const body = { name: form.name };
      if (form.owner_email) { body.owner_email = form.owner_email; body.owner_name = form.owner_name || form.name; }
      const { data } = await api.post("/admin/franchises", body);
      setResult(data);
      toast.success("Franchise created");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (f) => {
    setBusyId(f.id);
    const action = f.status === "active" ? "suspend" : "activate";
    try {
      const { data } = await api.post(`/admin/franchises/${f.id}/${action}`);
      setFranchises((prev) => prev.map((x) => (x.id === f.id ? { ...x, status: data.status } : x)));
      toast.success(`Franchise ${action}d`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const reset = () => { setForm({ name: "", owner_email: "", owner_name: "" }); setResult(null); };
  const copy = () => {
    if (!result?.owner_invite?.invite_url) return;
    navigator.clipboard.writeText(result.owner_invite.invite_url);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="super-admin-console">
      <TopNav />
      <div className="h-16 flex items-center justify-between px-8 bg-white border-b border-slate-200">
        <div>
          <h1 className="font-heading font-black text-xl tracking-tighter">Platform Admin</h1>
          <p className="text-xs text-slate-500">Manage franchisees across the platform</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
          <DialogTrigger asChild>
            <button data-testid="create-franchise-button"
              className={`flex items-center gap-2 bg-blue-700 text-white px-4 py-2 text-sm font-semibold rounded-none hover:bg-blue-800 transition-all ${tab === "franchises" ? "" : "invisible"}`}>
              <Plus className="h-4 w-4" /> New Franchise
            </button>
          </DialogTrigger>
          <DialogContent className="rounded-none sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tighter">Create a franchise</DialogTitle>
              <DialogDescription>Optionally invite the franchise owner (becomes a Franchise Admin).</DialogDescription>
            </DialogHeader>
            {result ? (
              <div className="py-2" data-testid="create-franchise-result">
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-3 py-2 mb-4">
                  Franchise "{result.franchise.name}" created.
                  {result.owner_invite ? " Owner invite generated below." : " No owner invited yet."}
                </div>
                {result.owner_invite && (
                  <>
                    <Lbl>Owner invite link</Lbl>
                    <div className="flex items-center gap-2 mt-1.5">
                      <input data-testid="franchise-owner-invite-link" readOnly value={result.owner_invite.invite_url}
                        className="flex-1 border border-slate-200 px-3 py-2 text-xs font-mono bg-slate-50 rounded-none" />
                      <button onClick={copy} className="border border-slate-200 p-2 hover:bg-slate-900 hover:text-white transition-all">
                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </button>
                    </div>
                  </>
                )}
                <DialogFooter className="mt-6">
                  <button onClick={() => { setOpen(false); reset(); }} className="bg-slate-900 text-white px-5 py-2 text-sm font-semibold rounded-none hover:bg-slate-800">Done</button>
                </DialogFooter>
              </div>
            ) : (
              <>
                <div className="space-y-4 py-2">
                  <label className="block space-y-1.5"><Lbl>Franchise name</Lbl>
                    <Inp data-testid="franchise-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. MDIJDM" />
                  </label>
                  <label className="block space-y-1.5"><Lbl>Owner email (optional)</Lbl>
                    <Inp data-testid="franchise-owner-email" type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} placeholder="owner@company.com" />
                  </label>
                  <label className="block space-y-1.5"><Lbl>Owner name (optional)</Lbl>
                    <Inp data-testid="franchise-owner-name" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} placeholder="Jane Doe" />
                  </label>
                </div>
                <DialogFooter>
                  <button data-testid="submit-franchise" onClick={create} disabled={saving || !form.name}
                    className="bg-blue-700 text-white px-5 py-2 text-sm font-semibold rounded-none hover:bg-blue-800 disabled:opacity-50">
                    {saving ? "Creating…" : "Create franchise"}
                  </button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="px-8 pt-6">
        <div className="flex gap-1 border-b border-slate-200">
          <button data-testid="admin-tab-franchises" onClick={() => setTab("franchises")}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-all ${tab === "franchises" ? "border-blue-700 text-blue-800" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            <Layers className="h-4 w-4" /> Franchises
          </button>
          <button data-testid="admin-tab-lab" onClick={() => setTab("lab")}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-all ${tab === "lab" ? "border-blue-700 text-blue-800" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            <FlaskConical className="h-4 w-4" /> Feature Lab
          </button>
        </div>
      </div>

      {tab === "lab" ? (
        <div className="px-8 py-6"><FeatureLab /></div>
      ) : (
      <div className="px-8 py-6 space-y-6">
        {overview && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard testid="ov-franchises" icon={Layers} label="Franchises" value={overview.franchises} />
            <StatCard testid="ov-active" icon={PlayCircle} label="Active" value={overview.active_franchises} />
            <StatCard testid="ov-stores" icon={Store} label="Stores" value={overview.stores} />
            <StatCard testid="ov-users" icon={Users} label="Users" value={overview.users} />
          </div>
        )}

        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-widest text-slate-500">
                <th className="px-4 py-3 font-medium">Franchise</th>
                <th className="px-4 py-3 font-medium">Owner</th>
                <th className="px-4 py-3 font-medium text-center">Stores</th>
                <th className="px-4 py-3 font-medium text-center">Users</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="franchises-table">
              {franchises.map((f) => (
                <tr key={f.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`franchise-row-${f.id}`}>
                  <td className="px-4 py-3 font-medium">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 bg-slate-900 text-white flex items-center justify-center"><Building2 className="h-4 w-4" /></div>
                      <div><div>{f.name}</div><div className="text-xs text-slate-400 font-mono">{f.slug}</div></div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{f.owner_email || "—"}</td>
                  <td className="px-4 py-3 text-center">{f.store_count}</td>
                  <td className="px-4 py-3 text-center">{f.user_count}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs border px-2 py-0.5 ${f.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>{f.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`toggle-franchise-${f.id}`} onClick={() => toggle(f)} disabled={busyId === f.id}
                      className={`inline-flex items-center gap-1.5 border px-3 py-1.5 text-xs font-semibold transition-all disabled:opacity-50 ${f.status === "active" ? "border-red-200 text-red-600 hover:bg-red-600 hover:text-white" : "border-emerald-200 text-emerald-700 hover:bg-emerald-600 hover:text-white"}`}>
                      {f.status === "active" ? <><Power className="h-3.5 w-3.5" /> Suspend</> : <><PlayCircle className="h-3.5 w-3.5" /> Activate</>}
                    </button>
                  </td>
                </tr>
              ))}
              {franchises.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500" data-testid="franchises-empty">No franchises yet. Create your first one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
}

function FeatureLab() {
  const { refresh } = useFeatures();
  const [flags, setFlags] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ key: "", name: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [betaInput, setBetaInput] = useState({});

  const load = () => api.get("/admin/features").then(({ data }) => setFlags(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setState = async (key, state) => {
    try {
      await api.patch(`/admin/features/${key}`, { state });
      toast.success(`Rollout set to "${state}"`);
      load(); refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const saveBeta = async (key) => {
    const codes = (betaInput[key] || "").split(",").map((s) => s.trim()).filter(Boolean);
    try {
      await api.patch(`/admin/features/${key}`, { beta_store_codes: codes });
      toast.success("Beta stores updated"); load(); refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/admin/features", form);
      toast.success("Feature flag created");
      setOpen(false); setForm({ key: "", name: "", description: "" }); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (key) => {
    try { await api.delete(`/admin/features/${key}`); toast.success("Flag deleted"); load(); refresh(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const STATE_META = {
    off: { label: "Off", cls: "bg-slate-100 text-slate-500 border-slate-200" },
    admin_preview: { label: "Admin Preview", cls: "bg-purple-50 text-purple-700 border-purple-200" },
    beta: { label: "Beta", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    on: { label: "Live (everyone)", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  };

  return (
    <div data-testid="feature-lab" className="space-y-5">
      <div className="flex items-start justify-between">
        <p className="text-sm text-slate-500 max-w-2xl">
          Test new features safely. Advance each flag through its lifecycle: <b>Off → Admin Preview</b> (only you see it) <b>→ Beta</b> (selected stores) <b>→ Live</b> (everyone). Flip back to Off anytime as a kill-switch.
        </p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="create-flag-button" className="flex items-center gap-2 bg-blue-700 text-white px-4 py-2 text-sm font-semibold rounded-none hover:bg-blue-800 whitespace-nowrap"><Plus className="h-4 w-4" /> New Flag</button>
          </DialogTrigger>
          <DialogContent className="rounded-none sm:max-w-lg">
            <DialogHeader><DialogTitle className="font-heading tracking-tighter">Create a feature flag</DialogTitle><DialogDescription>Wrap new UI behind this flag to roll it out gradually.</DialogDescription></DialogHeader>
            <div className="space-y-4 py-2">
              <label className="block space-y-1.5"><Lbl>Key</Lbl><Inp data-testid="flag-key" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="e.g. community_feed" /></label>
              <label className="block space-y-1.5"><Lbl>Name</Lbl><Inp data-testid="flag-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Community Feed" /></label>
              <label className="block space-y-1.5"><Lbl>Description</Lbl><Inp data-testid="flag-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What does this feature do?" /></label>
            </div>
            <DialogFooter><button data-testid="submit-flag" onClick={create} disabled={saving || !form.key || !form.name} className="bg-blue-700 text-white px-5 py-2 text-sm font-semibold rounded-none hover:bg-blue-800 disabled:opacity-50">{saving ? "Creating…" : "Create flag"}</button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3" data-testid="flags-list">
        {flags.map((f) => (
          <div key={f.key} className="bg-white border border-slate-200 p-4" data-testid={`flag-${f.key}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{f.name}</span>
                  <span className="text-xs font-mono text-slate-400">{f.key}</span>
                  <span className={`text-xs border px-2 py-0.5 ${STATE_META[f.state]?.cls}`}>{STATE_META[f.state]?.label}</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">{f.description}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="w-44">
                  <Select value={f.state} onValueChange={(v) => setState(f.key, v)}>
                    <SelectTrigger data-testid={`flag-state-${f.key}`} className="rounded-none h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="off">Off</SelectItem>
                      <SelectItem value="admin_preview">Admin Preview</SelectItem>
                      <SelectItem value="beta">Beta</SelectItem>
                      <SelectItem value="on">Live (everyone)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <button data-testid={`delete-flag-${f.key}`} onClick={() => remove(f.key)} title="Delete flag" className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            {f.state === "beta" && (
              <div className="mt-3 flex items-center gap-2">
                <Lbl>Beta store codes</Lbl>
                <input data-testid={`flag-beta-${f.key}`} defaultValue={(f.beta_store_codes || []).join(", ")} onChange={(e) => setBetaInput({ ...betaInput, [f.key]: e.target.value })}
                  className="flex-1 border border-slate-200 px-3 py-1.5 text-xs rounded-none" placeholder="18177, 18470" />
                <button onClick={() => saveBeta(f.key)} className="border border-slate-200 px-3 py-1.5 text-xs font-semibold hover:bg-slate-900 hover:text-white">Save</button>
              </div>
            )}
          </div>
        ))}
        {flags.length === 0 && <div className="text-center text-slate-500 py-12" data-testid="flags-empty">No feature flags yet.</div>}
      </div>
    </div>
  );
}
